import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Button,
    Space,
    Typography,
    message,
    Tag,
    Empty,
    Tooltip
} from 'antd';
import {
    HeartFilled,
    HeartOutlined,
    EyeOutlined
} from '@ant-design/icons';
import moment from 'moment';
import favoriteService from '../../services/favoriteService';
import DashboardLayout from '../Dashboard/DashboardLayout';
import PageHeader from '../Common/PageHeader';

const { Title } = Typography;

const FavoriteLots = () => {
    const [lots, setLots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const navigate = useNavigate();

    useEffect(() => {
        loadFavoriteLots();
    }, [pagination.current, pagination.pageSize]);

    const loadFavoriteLots = async () => {
        try {
            setLoading(true);
            const response = await favoriteService.getFavoriteLots({
                page: pagination.current,
                per_page: pagination.pageSize
            });
            
            setLots(response.data);
            setPagination(prev => ({
                ...prev,
                total: response.meta.total
            }));
        } catch (error) {
            console.error('Error loading favorite lots:', error);
            message.error('Failed to load favorite lots');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleFavorite = async (lot) => {
        try {
            // Optimistically update the UI
            setLots(prevLots => prevLots.filter(l => l.id !== lot.id));
            setPagination(prev => ({
                ...prev,
                total: prev.total - 1
            }));
            
            // Make the API call
            await favoriteService.toggleFavoriteLot(lot.id);
            message.success('Removed from favorites');
        } catch (error) {
            // Revert the optimistic update on error
            setLots(prevLots => [...prevLots, lot]);
            setPagination(prev => ({
                ...prev,
                total: prev.total + 1
            }));
            console.error('Error toggling favorite:', error);
            message.error('Failed to remove from favorites');
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'default',
            active: 'processing',
            sold: 'success',
            closed: 'success', // Closed lots are sold
            unsold: 'error'
        };
        return colors[status] || 'default';
    };

    const getStatusText = (status) => {
        // Map 'closed' to 'SOLD' for display
        if (status === 'closed') {
            return 'SOLD';
        }
        return status.toUpperCase();
    };

    const handleTableChange = (pagination) => {
        setPagination(pagination);
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            width: 200,
            render: (text, record) => (
                <Tooltip title={text} placement="top">
                    <a 
                        href={`/auctions/${record.auction.slug}/lots/${record.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                            color: '#1890ff',
                            textDecoration: 'none',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.textDecoration = 'none';
                        }}
                    >
                        {text.length > 30 ? `${text.substring(0, 30)}...` : text}
                    </a>
                </Tooltip>
            )
        },
        {
            title: 'Auction',
            dataIndex: ['auction', 'title'],
            key: 'auction',
            width: 200,
            render: (text, record) => (
                <Tooltip title={text} placement="top">
                    <a 
                        href={`/auctions/${record.auction.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                            color: '#1890ff',
                            textDecoration: 'none',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.textDecoration = 'none';
                        }}
                    >
                        {text && text.length > 30 ? `${text.substring(0, 30)}...` : text || 'N/A'}
                    </a>
                </Tooltip>
            )
        },
        {
            title: 'Starting Price',
            dataIndex: 'starting_price',
            key: 'starting_price',
            width: 120,
            render: (price) => price ? `$${Number(price).toFixed(2)}` : '$0.00'
        },
        {
            title: 'Current Price',
            dataIndex: 'current_price',
            key: 'current_price',
            width: 120,
            render: (_, record) => {
                const winningBid = record.winning_bid;
                const price = winningBid ? winningBid.amount : record.starting_price;
                return price ? `$${Number(price).toFixed(2)}` : '$0.00';
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {getStatusText(status)}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        icon={<HeartFilled style={{ color: '#ff4d4f' }} />}
                        onClick={() => handleToggleFavorite(record)}
                    />
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/auctions/${record.auction.slug}/lots/${record.slug}`)}
                    />
                </Space>
            )
        }
    ];

    return (
        <DashboardLayout>
            <PageHeader
                title="Favorite Lots"
                description="View and manage your favorite auction lots"
            />
            <Table
                columns={columns}
                dataSource={lots}
                rowKey="id"
                pagination={pagination}
                loading={loading}
                onChange={handleTableChange}
                scroll={{ x: 1000 }}
                locale={{
                    emptyText: loading 
                        ? null // Don't show empty text when loading
                        : (
                            <Empty
                                description="No favorite lots yet"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        )
                }}
            />
        </DashboardLayout>
    );
};

export default FavoriteLots; 